import { BlockType } from "../world/block-registry";
import { ItemType } from "./items";
import type { Inventory } from "./inventory";

export type CraftIngredient = {
  id: number;
  count: number;
};

export type Recipe = {
  id: string;
  name: string;
  inputs: CraftIngredient[];
  output: CraftIngredient;
  /** Requires standing near a crafting table block. */
  requiresTable?: boolean;
  /** Smelting — requires standing near a furnace block. */
  requiresFurnace?: boolean;
};

export const RECIPES: Recipe[] = [
  {
    id: "planks",
    name: "Tábuas",
    inputs: [{ id: BlockType.WOOD, count: 1 }],
    output: { id: BlockType.PLANKS, count: 4 },
  },
  {
    id: "sticks",
    name: "Gravetos",
    inputs: [{ id: BlockType.PLANKS, count: 2 }],
    output: { id: ItemType.STICK, count: 4 },
  },
  {
    id: "crafting_table",
    name: "Mesa de trabalho",
    inputs: [{ id: BlockType.PLANKS, count: 4 }],
    output: { id: BlockType.CRAFTING_TABLE, count: 1 },
  },
  {
    id: "chest",
    name: "Baú",
    inputs: [{ id: BlockType.PLANKS, count: 8 }],
    output: { id: BlockType.CHEST, count: 1 },
  },
  {
    id: "glass",
    name: "Vidro",
    inputs: [{ id: BlockType.SAND, count: 1 }],
    output: { id: BlockType.GLASS, count: 1 },
  },
  {
    id: "stone",
    name: "Pedra polida",
    inputs: [{ id: BlockType.COBBLESTONE, count: 1 }],
    output: { id: BlockType.STONE, count: 1 },
  },
  {
    id: "apple",
    name: "Maçã",
    inputs: [{ id: BlockType.LEAVES, count: 4 }],
    output: { id: ItemType.APPLE, count: 1 },
  },
  // ─── Tools (need a crafting table nearby, Minecraft-style) ───
  {
    id: "wooden_pickaxe",
    name: "Picareta de madeira",
    inputs: [{ id: BlockType.PLANKS, count: 3 }, { id: ItemType.STICK, count: 2 }],
    output: { id: ItemType.WOODEN_PICKAXE, count: 1 },
    requiresTable: true,
  },
  {
    id: "wooden_axe",
    name: "Machado de madeira",
    inputs: [{ id: BlockType.PLANKS, count: 3 }, { id: ItemType.STICK, count: 2 }],
    output: { id: ItemType.WOODEN_AXE, count: 1 },
    requiresTable: true,
  },
  {
    id: "wooden_shovel",
    name: "Pá de madeira",
    inputs: [{ id: BlockType.PLANKS, count: 1 }, { id: ItemType.STICK, count: 2 }],
    output: { id: ItemType.WOODEN_SHOVEL, count: 1 },
    requiresTable: true,
  },
  {
    id: "wooden_sword",
    name: "Espada de madeira",
    inputs: [{ id: BlockType.PLANKS, count: 2 }, { id: ItemType.STICK, count: 1 }],
    output: { id: ItemType.WOODEN_SWORD, count: 1 },
    requiresTable: true,
  },
  {
    id: "stone_pickaxe",
    name: "Picareta de pedra",
    inputs: [{ id: BlockType.COBBLESTONE, count: 3 }, { id: ItemType.STICK, count: 2 }],
    output: { id: ItemType.STONE_PICKAXE, count: 1 },
    requiresTable: true,
  },
  {
    id: "stone_axe",
    name: "Machado de pedra",
    inputs: [{ id: BlockType.COBBLESTONE, count: 3 }, { id: ItemType.STICK, count: 2 }],
    output: { id: ItemType.STONE_AXE, count: 1 },
    requiresTable: true,
  },
  {
    id: "stone_shovel",
    name: "Pá de pedra",
    inputs: [{ id: BlockType.COBBLESTONE, count: 1 }, { id: ItemType.STICK, count: 2 }],
    output: { id: ItemType.STONE_SHOVEL, count: 1 },
    requiresTable: true,
  },
  {
    id: "stone_sword",
    name: "Espada de pedra",
    inputs: [{ id: BlockType.COBBLESTONE, count: 2 }, { id: ItemType.STICK, count: 1 }],
    output: { id: ItemType.STONE_SWORD, count: 1 },
    requiresTable: true,
  },
  {
    id: "furnace",
    name: "Fornalha",
    inputs: [{ id: BlockType.COBBLESTONE, count: 8 }],
    output: { id: BlockType.FURNACE, count: 1 },
    requiresTable: true,
  },
  // ─── Smelting (need a furnace + 1 carvão de combustível) ───
  {
    id: "cook_porkchop",
    name: "Costela assada",
    inputs: [{ id: ItemType.PORKCHOP, count: 1 }, { id: ItemType.COAL, count: 1 }],
    output: { id: ItemType.COOKED_PORKCHOP, count: 1 },
    requiresFurnace: true,
  },
  {
    id: "cook_beef",
    name: "Bife assado",
    inputs: [{ id: ItemType.BEEF, count: 1 }, { id: ItemType.COAL, count: 1 }],
    output: { id: ItemType.COOKED_BEEF, count: 1 },
    requiresFurnace: true,
  },
  {
    id: "cook_chicken",
    name: "Frango assado",
    inputs: [{ id: ItemType.CHICKEN_MEAT, count: 1 }, { id: ItemType.COAL, count: 1 }],
    output: { id: ItemType.COOKED_CHICKEN, count: 1 },
    requiresFurnace: true,
  },
  {
    id: "iron_ingot",
    name: "Lingote de ferro",
    inputs: [{ id: BlockType.IRON_ORE, count: 1 }, { id: ItemType.COAL, count: 1 }],
    output: { id: ItemType.IRON_INGOT, count: 1 },
    requiresFurnace: true,
  },
  // ─── Iron tools (crafting table + lingotes de ferro) ───
  {
    id: "iron_pickaxe",
    name: "Picareta de ferro",
    inputs: [{ id: ItemType.IRON_INGOT, count: 3 }, { id: ItemType.STICK, count: 2 }],
    output: { id: ItemType.IRON_PICKAXE, count: 1 },
    requiresTable: true,
  },
  {
    id: "iron_axe",
    name: "Machado de ferro",
    inputs: [{ id: ItemType.IRON_INGOT, count: 3 }, { id: ItemType.STICK, count: 2 }],
    output: { id: ItemType.IRON_AXE, count: 1 },
    requiresTable: true,
  },
  {
    id: "iron_shovel",
    name: "Pá de ferro",
    inputs: [{ id: ItemType.IRON_INGOT, count: 1 }, { id: ItemType.STICK, count: 2 }],
    output: { id: ItemType.IRON_SHOVEL, count: 1 },
    requiresTable: true,
  },
  {
    id: "iron_sword",
    name: "Espada de ferro",
    inputs: [{ id: ItemType.IRON_INGOT, count: 2 }, { id: ItemType.STICK, count: 1 }],
    output: { id: ItemType.IRON_SWORD, count: 1 },
    requiresTable: true,
  },
  // ─── Diamond tools (crafting table + diamantes) ───
  {
    id: "diamond_pickaxe",
    name: "Picareta de diamante",
    inputs: [{ id: ItemType.DIAMOND_GEM, count: 3 }, { id: ItemType.STICK, count: 2 }],
    output: { id: ItemType.DIAMOND_PICKAXE, count: 1 },
    requiresTable: true,
  },
  {
    id: "diamond_axe",
    name: "Machado de diamante",
    inputs: [{ id: ItemType.DIAMOND_GEM, count: 3 }, { id: ItemType.STICK, count: 2 }],
    output: { id: ItemType.DIAMOND_AXE, count: 1 },
    requiresTable: true,
  },
  {
    id: "diamond_shovel",
    name: "Pá de diamante",
    inputs: [{ id: ItemType.DIAMOND_GEM, count: 1 }, { id: ItemType.STICK, count: 2 }],
    output: { id: ItemType.DIAMOND_SHOVEL, count: 1 },
    requiresTable: true,
  },
  {
    id: "diamond_sword",
    name: "Espada de diamante",
    inputs: [{ id: ItemType.DIAMOND_GEM, count: 2 }, { id: ItemType.STICK, count: 1 }],
    output: { id: ItemType.DIAMOND_SWORD, count: 1 },
    requiresTable: true,
  },
];

export function canCraft(
  inventory: Inventory,
  recipe: Recipe,
  nearTable = true,
  nearFurnace = true
): boolean {
  if (recipe.requiresTable && !nearTable) return false;
  if (recipe.requiresFurnace && !nearFurnace) return false;
  return recipe.inputs.every((ing) => inventory.getCount(ing.id) >= ing.count);
}

/**
 * Consumes the ingredients and adds the output. Capacity is checked
 * BEFORE mutating anything — addItem applies partially on failure, so
 * a remove-then-rollback approach could duplicate items.
 */
export function craft(
  inventory: Inventory,
  recipe: Recipe,
  nearTable = true,
  nearFurnace = true
): boolean {
  if (!canCraft(inventory, recipe, nearTable, nearFurnace)) return false;

  for (const ing of recipe.inputs) {
    inventory.removeItem(ing.id, ing.count);
  }

  // Removing the inputs can only free space, so this either fit before
  // or fits now — but verify to stay safe against weird recipes.
  if (!inventory.canFit(recipe.output.id, recipe.output.count)) {
    for (const ing of recipe.inputs) {
      inventory.addItem(ing.id, ing.count);
    }
    return false;
  }

  inventory.addItem(recipe.output.id, recipe.output.count);
  return true;
}
